import Label from '@/components/core/label';
import { ChoicesOrderItemLabel } from '@/types/order';
import { RHFSelect } from '@components/forms/hook-form';
import { Card, Grid, MenuItem, CardHeader, Typography, CardContent } from '@mui/material';
import { useMemo, useEffect, ChangeEvent } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import { ChoicesOrderItemType, ChoicesOrderUnitMeasure } from '../../../orders/types';
import { useBillingStore } from '../../store/billing.store';
import { groupUniqueItems, groupUniqueUnitMeasureDistribution } from '../../utils/billing.utils';
import { SecondStepFormValues } from '../../types/billing-form.types';

interface DistributionUnitSelectorProps {
  visible?: boolean;
}

export const DistributionUnitSelector = ({ visible = true }: DistributionUnitSelectorProps) => {
  const { t } = useTranslation();
  const selectedMilestones = useBillingStore((state) => state.selectedMilestones);

  const groupedUniqueItems = useMemo(() => {
    return groupUniqueItems(selectedMilestones).sort((a, b) => a.sku.localeCompare(b.sku));
  }, [selectedMilestones]);

  const { setValue, control } = useFormContext();

  const formValues: SecondStepFormValues[] = useWatch({
    name: 'formValues',
    control,
  });

  const unitMeasureDistribution = useWatch({ name: 'unitMeasureDistribution', control });

  const unitMesureChoices = [
    { value: ChoicesOrderUnitMeasure.quantity, label: t('Quantity') },
    {
      value: ChoicesOrderUnitMeasure.percentageAmount,
      label: t('Percentage of the amount'),
    },
    { value: ChoicesOrderUnitMeasure.amount, label: t('Amount') },
  ];

  useEffect(() => {
    if (groupedUniqueItems.length) {
      const unitMeasureDistribution = groupUniqueUnitMeasureDistribution(groupedUniqueItems);
      setValue('unitMeasureDistribution', unitMeasureDistribution);
    }
  }, [groupedUniqueItems]);

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    itemKey: string
  ) => {
    const { value } = e.target;
    const newUnitMeasureDistribution: Record<string, ChoicesOrderUnitMeasure> =
      unitMeasureDistribution;
    newUnitMeasureDistribution[itemKey] = value as ChoicesOrderUnitMeasure;
    let newFormValues = formValues.map((formValue) => {
      return {
        documents: formValue.documents,
        legalEntity: formValue.legalEntity,
        items: formValue.items.map((item) => {
          return {
            ...item,
            unitMeasure:
              `${item.itemInfo.subscription}-${item.itemInfo.type}` === itemKey
                ? value
                : item.unitMeasure,
          };
        }),
      };
    });
    setValue('unitMeasureDistribution', newUnitMeasureDistribution);
    setValue('formValues', newFormValues);
  };

  if (!visible) return <div></div>;
  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader title={t('Distribution unit')} />
      <CardContent>
        <Grid container spacing={1} sx={{ pt: 0 }}>
          {groupedUniqueItems.map((item, index) => (
            <Fragment key={`${item.subscription}-${item.type}}`}>
              <Grid item xs={8} alignContent="center">
                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                  {item.productName} - {item.sku}
                  <Label
                    sx={{ ml: 2 }}
                    color={item.type === ChoicesOrderItemType.newResource ? 'primary' : 'secondary'}
                    variant="outlined"
                  >
                    {t(ChoicesOrderItemLabel[item.type])}
                  </Label>
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <RHFSelect
                  onChange={(e) => {
                    handleChange(e, `${item.subscription}-${item.type}`);
                  }}
                  sx={{ mb: 1 }}
                  name={`unitMeasureDistribution.${item.subscription}-${item.type}`}
                  label={t('Distribution unit')}
                  size="small"
                >
                  {unitMesureChoices.map((choice, index) => (
                    <MenuItem key={`${index}-${choice.value}`} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Grid>
            </Fragment>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};
